import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { useTranslation } from 'react-i18next';
import { useShowView } from '@stores/calibrationUIStore';
import {
  getSectionHeaderSx,
  getSubHeaderSx,
  getStickyHeaderSx
} from '@features/calibration/components/shared/utils';
import type { DatasheetGroup } from '@features/calibration/hooks/useGroupedDatasheet';
import type { CommitMeasurementHandler, RunnerLiveState } from '../types';
import RunnerRow from './RunnerRow';

type RunnerTableProps = {
  group: DatasheetGroup;
  readOnly?: boolean;
  liveState?: RunnerLiveState;
  runId?: string;
  onCommitMeasurement?: CommitMeasurementHandler;
};

const RunnerTable: React.FC<RunnerTableProps> = ({
  group,
  readOnly = false,
  liveState,
  runId,
  onCommitMeasurement
}) => {
  const { t } = useTranslation('calibration');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const showView = useShowView();

  const showAsFound = showView === 'all' || showView === 'asFound';
  const showAsLeft = showView === 'all' || showView === 'asLeft';

  const renderSubHeaderCells = (section: 'asFound' | 'asLeft') => (
    <>
      <TableCell sx={getSubHeaderSx(section, isDarkMode)} align="center">
        {t('testGrid.measured')}
      </TableCell>
      <TableCell sx={getSubHeaderSx(section, isDarkMode)} align="center">
        {t('testGrid.error')}
      </TableCell>
      <TableCell sx={getSubHeaderSx(section, isDarkMode)} align="center">
        {t('testGrid.result')}
      </TableCell>
      <TableCell
        sx={getSubHeaderSx(section, isDarkMode, '40px', 99, true)}
        align="center"
      >
        <CameraAltOutlinedIcon
          fontSize="small"
          sx={{ verticalAlign: 'middle' }}
        />
      </TableCell>
    </>
  );

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={getStickyHeaderSx('neutral', isDarkMode)} />
            {showAsFound && (
              <TableCell
                colSpan={4}
                align="center"
                sx={getSectionHeaderSx('asFound', isDarkMode)}
              >
                {t('testGrid.asFound')}
              </TableCell>
            )}
            {showAsLeft && (
              <TableCell
                colSpan={4}
                align="center"
                sx={getSectionHeaderSx('asLeft', isDarkMode)}
              >
                {t('testGrid.asLeft')}
              </TableCell>
            )}
            <TableCell sx={getStickyHeaderSx('neutral', isDarkMode)} />
          </TableRow>
          <TableRow>
            <TableCell
              sx={getStickyHeaderSx('neutral', isDarkMode, '40px', 99)}
            >
              {t('testGrid.nominal')}
            </TableCell>
            {showAsFound && renderSubHeaderCells('asFound')}
            {showAsLeft && renderSubHeaderCells('asLeft')}
            <TableCell
              sx={getStickyHeaderSx('neutral', isDarkMode, '40px', 99)}
            >
              {t('testGrid.tolerance')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {group.points.map(point => (
            <RunnerRow
              key={point.id}
              point={point}
              unit={group.unit}
              showAsFound={showAsFound}
              showAsLeft={showAsLeft}
              readOnly={readOnly}
              isCurrentPoint={liveState?.currentPointId === point.id}
              landedSide={
                liveState?.landedPointId === point.id
                  ? liveState.landedSide
                  : null
              }
              landedAt={
                liveState?.landedPointId === point.id
                  ? liveState.landedAt
                  : null
              }
              runId={runId}
              onCommitMeasurement={onCommitMeasurement}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RunnerTable;
